import DOMPurify from 'dompurify';

import { ReportComponentType } from '@/types';
import { DescriptionConfig, ReportComponentConfig } from '@/types/api/modules/business-intelligence';

const REPORT_DESCRIPTION_MAX_LENGTH = 200_000;

const DESCRIPTION_ALLOWED_TAGS = [
    'a',
    'blockquote',
    'br',
    'code',
    'div',
    'em',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'iframe',
    'img',
    'li',
    'mark',
    'ol',
    'p',
    'pre',
    's',
    'span',
    'strong',
    'u',
    'ul',
];

const DESCRIPTION_ALLOWED_ATTRIBUTES = [
    'allow',
    'allowfullscreen',
    'alt',
    'class',
    'frameborder',
    'height',
    'href',
    'rel',
    'src',
    'style',
    'target',
    'title',
    'type',
    'width',
];

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

const looksLikeHtml = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

export const textToReportDescriptionHtml = (value: string) => {
    const paragraphs = value
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean);

    if (!paragraphs.length) {
        return '';
    }

    return paragraphs.map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br />')}</p>`).join('');
};

export const sanitizeReportDescriptionHtml = (value: string) =>
    DOMPurify.sanitize(value.slice(0, REPORT_DESCRIPTION_MAX_LENGTH), {
        ALLOWED_TAGS: DESCRIPTION_ALLOWED_TAGS,
        ALLOWED_ATTR: DESCRIPTION_ALLOWED_ATTRIBUTES,
        ADD_ATTR: ['target'],
    });

export const resolveReportDescriptionBody = (
    configuration?: Partial<DescriptionConfig>,
    fallbackDescription?: string,
) => {
    const rawBody = configuration?.body ?? configuration?.markdown ?? fallbackDescription ?? '';
    const html = looksLikeHtml(rawBody) ? rawBody : textToReportDescriptionHtml(rawBody);

    return sanitizeReportDescriptionHtml(html);
};

export const normalizeReportDescriptionConfiguration = (
    configuration: Partial<DescriptionConfig> | undefined,
    fallbackDescription?: string,
): DescriptionConfig => {
    const body = resolveReportDescriptionBody(configuration, fallbackDescription);
    const { markdown: _legacyMarkdown, ...rest } = configuration ?? {};

    return {
        ...rest,
        version: rest.version ?? 1,
        type: ReportComponentType.DESCRIPTION,
        body,
    } as DescriptionConfig;
};

export const isDescriptionConfig = (
    configuration: Partial<ReportComponentConfig> | undefined,
): configuration is Partial<DescriptionConfig> => configuration?.type === ReportComponentType.DESCRIPTION;
